import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  AlertTriangle, ArrowRight, ArrowUpRight, CheckCircle2, Loader2, MapPin,
  RefreshCw, Search, ShieldAlert, Trash2, Wrench,
} from "lucide-react";

/**
 * SuperAdmin — City Integrity data-quality view.
 *
 * Surfaces the vendor + campaign records that could NOT be auto-
 * backfilled with a canonical ``city_id`` during Phase 20. Split into
 * two purpose-built tabs:
 *
 *   • "Needs city"    — real records with a valid ``id`` but no
 *     resolvable city on either the record or its owning vendor.
 *     Fix is a one-click city dropdown per row.
 *
 *   • "Needs review"  — broken records (missing ``id``, or garbage
 *     city string like 'B'/'X'). Super Admin must decide: **repair**
 *     (generate id + assign city in one step) or **delete** the doc
 *     entirely. Never auto-mutated.
 *
 * Data comes from ``GET /superadmin/data-quality/city-integrity``.
 * Every action posts to ``POST /superadmin/data-quality/city-integrity/resolve``.
 */
export default function SuperAdminCityIntegrityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busyRefs, setBusyRefs] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");

  // Deep-link support: ?tab=needs-review lands on the review pane
  // directly from the dashboard widget. Any other value falls back
  // to the safe default. The Tabs component below is fully
  // controlled off this value.
  const rawTab = searchParams.get("tab");
  const activeTab = rawTab === "needs-review" ? "needs-review" : "needs-city";
  const setActiveTab = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v && v !== "needs-city") next.set("tab", v); else next.delete("tab");
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setErr(null);
    setLoading(true);
    try {
      const r = await api.superadminCityIntegrityList();
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markBusy = (ref, on) =>
    setBusyRefs((s) => {
      const next = new Set(s);
      if (on) next.add(ref);
      else next.delete(ref);
      return next;
    });

  const resolve = async (collection, ref, action, cityId, reason) => {
    if (!ref) return;
    markBusy(ref, true);
    try {
      const payload = { collection, ref, action };
      if (cityId) payload.city_id = cityId;
      if (reason) payload.reason = reason;
      await api.superadminCityIntegrityResolve(payload);
      const label =
        action === "delete"
          ? "Record deleted"
          : action === "repair"
            ? "Record repaired"
            : "City assigned";
      toast.success(label);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Action failed");
    } finally {
      markBusy(ref, false);
    }
  };

  const cities = data?.cities || [];
  const cityByName = useMemo(() => {
    const m = new Map();
    cities.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cities]);

  const filteredList = (arr, keys) => {
    if (!filter.trim()) return arr;
    const q = filter.toLowerCase();
    return (arr || []).filter((row) =>
      keys.some((k) => String(row[k] || "").toLowerCase().includes(q))
    );
  };

  const openDelete = (collection, ref, label) => {
    setDeleteTarget({ collection, ref, label });
    setDeleteReason("");
  };

  return (
    <div className="space-y-6" data-testid="superadmin-city-integrity-page">
      <Toaster />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight">
            City integrity
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Records where Phase-20 <span className="font-mono">city_id</span> backfill
            couldn't resolve a canonical city. Fixing these here restores 100 % franchise
            scoping accuracy without touching the DB by hand.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="city-integrity-refresh-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <MapPin className="h-4 w-4" /> Needs city
          </div>
          <div className="text-3xl font-black tabular-nums mt-1" data-testid="needs-city-total">
            {data?.needs_city?.total ?? "—"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {data?.needs_city?.vendors?.length ?? 0} vendors ·{" "}
            {data?.needs_city?.campaigns?.length ?? 0} campaigns
          </div>
        </Card>
        <Card className="p-4 border-amber-300 border-2">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-700">
            <ShieldAlert className="h-4 w-4" /> Needs review
          </div>
          <div className="text-3xl font-black tabular-nums mt-1" data-testid="needs-review-total">
            {data?.needs_review?.total ?? "—"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {data?.needs_review?.vendors?.length ?? 0} vendors ·{" "}
            {data?.needs_review?.campaigns?.length ?? 0} campaigns
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <CheckCircle2 className="h-4 w-4" /> City catalog
          </div>
          <div className="text-3xl font-black tabular-nums mt-1">
            {cities.length}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Active cities available for assignment
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Filter by email, name, id, city…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pl-9"
          data-testid="city-integrity-filter"
        />
      </div>

      {err && (
        <Card className="p-4 border-red-300 border-2 text-red-700" data-testid="city-integrity-error">
          {String(err)}
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="needs-city" data-testid="tab-needs-city">
            Needs city ({data?.needs_city?.total ?? 0})
          </TabsTrigger>
          <TabsTrigger value="needs-review" data-testid="tab-needs-review">
            Needs review ({data?.needs_review?.total ?? 0})
          </TabsTrigger>
        </TabsList>

        {/* ─── Tab 1: Needs city ────────────────────────────────── */}
        <TabsContent value="needs-city" className="space-y-6">
          {loading ? (
            <SkeletonList />
          ) : (
            <>
              <SectionTable
                title="Vendors"
                rows={filteredList(data?.needs_city?.vendors, ["email", "business_name", "id", "mongo_oid", "phone"])}
                columns={[
                  { key: "business_name", label: "Business", render: (v) => v || <span className="text-muted-foreground italic">unset</span> },
                  { key: "email", label: "Email" },
                  { key: "id", label: "Vendor id", mono: true, truncate: 12 },
                ]}
                cities={cities}
                busyRefs={busyRefs}
                mode="assign"
                onAssign={(row, cityId) => resolve("vendors", row.id || row.mongo_oid, "assign_city", cityId)}
                onDelete={null}
              />
              <SectionTable
                title="Campaigns"
                rows={filteredList(data?.needs_city?.campaigns, ["name", "vendor_business_name", "vendor_email", "id", "mongo_oid"])}
                columns={[
                  { key: "name", label: "Campaign", render: (v) => v || <span className="text-muted-foreground italic">unnamed</span> },
                  { key: "vendor_business_name", label: "Vendor", render: (v) => v || <span className="text-muted-foreground italic">unknown</span> },
                  { key: "vendor_email", label: "Vendor email" },
                  { key: "status", label: "Status" },
                ]}
                cities={cities}
                busyRefs={busyRefs}
                mode="assign"
                onAssign={(row, cityId) => resolve("campaigns", row.id || row.mongo_oid, "assign_city", cityId)}
                onDelete={null}
              />
            </>
          )}
        </TabsContent>

        {/* ─── Tab 2: Needs review ──────────────────────────────── */}
        <TabsContent value="needs-review" className="space-y-6">
          <Card className="p-3 border-amber-300 border-2 bg-amber-50 text-amber-900 text-sm flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              These records are structurally broken (missing <span className="font-mono">id</span>{" "}
              field or holding garbage city strings). Repairing generates a fresh id AND assigns
              the city in one step. Deleting is permanent — a snapshot is kept in the admin log.
            </div>
          </Card>
          {loading ? (
            <SkeletonList />
          ) : (
            <>
              <SectionTable
                title="Vendors"
                rows={filteredList(data?.needs_review?.vendors, ["email", "business_name", "id", "mongo_oid", "raw_city"])}
                columns={[
                  { key: "business_name", label: "Business", render: (v) => v || <span className="text-muted-foreground italic">unset</span> },
                  { key: "email", label: "Email" },
                  { key: "raw_city", label: "Raw city", render: (v) => v
                    ? <span className="font-mono text-red-700">{v}</span>
                    : <span className="text-muted-foreground italic">null</span>,
                  },
                  { key: "reason", label: "Reason", render: (v) => <ReasonBadge value={v} /> },
                ]}
                cities={cities}
                busyRefs={busyRefs}
                mode="review"
                onRepair={(row, cityId) => resolve("vendors", row.id || row.mongo_oid, "repair", cityId)}
                onDelete={(row) => openDelete("vendors", row.id || row.mongo_oid, row.email || row.mongo_oid)}
              />
              <SectionTable
                title="Campaigns"
                rows={filteredList(data?.needs_review?.campaigns, ["name", "vendor_email", "id", "mongo_oid", "raw_campaign_city"])}
                columns={[
                  { key: "name", label: "Campaign", render: (v) => v || <span className="text-muted-foreground italic">unnamed</span> },
                  { key: "vendor_email", label: "Vendor email" },
                  { key: "raw_campaign_city", label: "Raw campaign city", render: (v) => v
                    ? <span className="font-mono text-red-700">{v}</span>
                    : <span className="text-muted-foreground italic">null</span>,
                  },
                  { key: "reason", label: "Reason", render: (v) => <ReasonBadge value={v} /> },
                ]}
                cities={cities}
                busyRefs={busyRefs}
                mode="review"
                onRepair={(row, cityId) => resolve("campaigns", row.id || row.mongo_oid, "repair", cityId)}
                onDelete={(row) => openDelete("campaigns", row.id || row.mongo_oid, row.name || row.mongo_oid)}
              />
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="city-integrity-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete record — {deleteTarget?.label}</DialogTitle>
            <DialogDescription>
              This permanently removes the record. A JSON snapshot is kept in{" "}
              <span className="font-mono">admin_logs</span> for audit. This action can't be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="del-reason">Reason (required)</Label>
            <Input
              id="del-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="e.g. leftover POC test data, no live traffic"
              data-testid="city-integrity-delete-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={!deleteReason.trim() || busyRefs.has(deleteTarget?.ref)}
              onClick={async () => {
                if (!deleteTarget) return;
                await resolve(deleteTarget.collection, deleteTarget.ref, "delete", null, deleteReason);
                setDeleteTarget(null);
              }}
              data-testid="city-integrity-delete-confirm"
            >
              {busyRefs.has(deleteTarget?.ref)
                ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                : <Trash2 className="h-4 w-4 mr-1" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function SkeletonList() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function ReasonBadge({ value }) {
  const map = {
    empty_city: { label: "empty city", tone: "muted" },
    missing_id_field: { label: "no id field", tone: "danger" },
    unresolvable_city_string: { label: "garbage city", tone: "danger" },
    resolvable_but_not_backfilled: { label: "backfill missed", tone: "warn" },
    empty_both_sides: { label: "empty both sides", tone: "muted" },
  };
  const cfg = map[value] || { label: value || "unknown", tone: "muted" };
  const cls =
    cfg.tone === "danger"
      ? "bg-red-100 text-red-800"
      : cfg.tone === "warn"
        ? "bg-amber-100 text-amber-800"
        : "bg-muted text-foreground";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${cls}`}>
      {cfg.label}
    </span>
  );
}

function SectionTable({
  title, rows, columns, cities, busyRefs, mode,
  onAssign, onRepair, onDelete,
}) {
  const [selected, setSelected] = useState({});

  if (!rows || !rows.length) {
    return (
      <Card className="p-4 text-sm text-muted-foreground" data-testid={`city-integrity-${title.toLowerCase()}-empty`}>
        No {title.toLowerCase()} to review.
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden" data-testid={`city-integrity-section-${title.toLowerCase()}`}>
      <div className="px-4 py-2 border-b bg-muted/50 font-semibold">
        {title} <Badge variant="secondary" className="ml-1">{rows.length}</Badge>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="px-3 py-2 text-left">{c.label}</th>
              ))}
              <th className="px-3 py-2 text-left">Assign city</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((row) => {
              const ref = row.id || row.mongo_oid;
              const busy = busyRefs.has(ref);
              const sel = selected[ref] || "";
              return (
                <tr key={ref} data-testid={`ci-row-${ref}`}>
                  {columns.map((c) => {
                    const v = row[c.key];
                    return (
                      <td key={c.key} className={`px-3 py-2 ${c.mono ? "font-mono text-xs" : ""}`}>
                        {c.render
                          ? c.render(v, row)
                          : c.truncate && v
                            ? `${String(v).slice(0, c.truncate)}…`
                            : (v ?? "—")}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2">
                    <Select
                      value={sel}
                      onValueChange={(val) => setSelected((s) => ({ ...s, [ref]: val }))}
                    >
                      <SelectTrigger className="h-8 w-40" data-testid={`ci-city-select-${ref}`}>
                        <SelectValue placeholder="Pick a city…" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-2">
                      {mode === "assign" ? (
                        <Button
                          size="sm"
                          disabled={!sel || busy}
                          onClick={() => onAssign(row, sel)}
                          data-testid={`ci-assign-btn-${ref}`}
                        >
                          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ArrowRight className="h-3 w-3 mr-1" />}
                          Assign
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            disabled={!sel || busy}
                            onClick={() => onRepair(row, sel)}
                            data-testid={`ci-repair-btn-${ref}`}
                          >
                            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wrench className="h-3 w-3 mr-1" />}
                            Repair
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={busy}
                            onClick={() => onDelete(row)}
                            data-testid={`ci-delete-btn-${ref}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
